import { useEffect, useState, type ChangeEvent } from "react";
import { useNavigate } from "react-router-dom";
import type { Organization } from "@scheduler/shared";
import { useAuth } from "../lib/auth";
import { api, setActiveOrgSlug } from "../lib/api";

/**
 * M7.1 — active-organization switcher.
 *
 * Lets the two roles that can hold more than one org context change which one
 * they're browsing:
 *   - super-admins, who observe every tenant (listed via GET /api/orgs), and
 *   - org-admins who own more than one org (listed via GET /api/orgs/mine).
 *
 * Single-org users never see the control. The choice is persisted in
 * lib/api.ts and sent as the `X-Org-Slug` header on every subsequent request;
 * after a switch we refresh /me (so the shell chrome follows) and return to
 * Home so no stale, other-org page is left on screen.
 */
export function OrgSwitcher() {
  const { user, activeOrg, refresh } = useAuth();
  const nav = useNavigate();
  const [orgs, setOrgs] = useState<Organization[] | null>(null);
  const [switching, setSwitching] = useState(false);

  const isSuper = user?.is_super_admin === true;
  const canSwitch = isSuper || user?.is_admin === true;

  useEffect(() => {
    if (!canSwitch) {
      setOrgs(null);
      return;
    }
    let cancelled = false;
    const load = isSuper ? api.listOrgs() : api.listMyOrgs();
    load
      .then((res) => {
        if (!cancelled) setOrgs(res.organizations);
      })
      .catch(() => {
        if (!cancelled) setOrgs([]);
      });
    return () => {
      cancelled = true;
    };
  }, [canSwitch, isSuper]);

  // Super-admins always get the control (they can land in any tenant);
  // org-admins only when they actually own more than one org.
  const show =
    canSwitch && orgs !== null && (isSuper || orgs.length > 1);
  if (!show || orgs === null) return null;

  async function onChange(e: ChangeEvent<HTMLSelectElement>) {
    const slug = e.target.value || null;
    setSwitching(true);
    setActiveOrgSlug(slug);
    try {
      await refresh();
      nav("/");
    } finally {
      setSwitching(false);
    }
  }

  return (
    <label className="flex min-w-0 items-center">
      <span className="sr-only">Active organization</span>
      <select
        value={activeOrg?.slug ?? ""}
        onChange={onChange}
        disabled={switching}
        aria-label="Switch active organization"
        className="max-w-[44vw] truncate rounded-md border border-ink-3 bg-ink-2
                   px-2 py-1.5 text-xs font-medium text-fg-1 transition-colors
                   hover:text-fg-0 focus:border-accent focus:outline-none
                   focus:ring-1 focus:ring-accent/30 disabled:opacity-60
                   sm:max-w-[12rem] sm:text-sm"
      >
        {isSuper ? <option value="">All organizations</option> : null}
        {orgs.map((o) => (
          <option key={o.id} value={o.slug}>
            {o.name}
          </option>
        ))}
      </select>
    </label>
  );
}
